// src/pages/interview/InterviewActivePage.js
import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import QuestionDisplay from "../../components/QuestionDisplay"; // Assume this will be themed or is theme-agnostic
import VoiceRecorder from "../../components/VoiceRecorder"; // Assume this will be themed or is theme-agnostic
import FeedbackPanel from "../../components/FeedbackPanel"; // Assume this will be themed or is theme-agnostic
import Loader from "../../components/Loader";
import { useInterviewContext } from "../../contexts/InterviewContext";
import {
  evaluateAnswer,
  getOverallInterviewFeedback,
} from "../../services/interviewAssistantService";

const MAX_RETRIES = 3;

const InterviewActivePage = () => {
  const {
    cvData,
    questions,
    currentQuestionIndex,
    setCurrentQuestionIndex,
    userResponses,
    addResponse,
    addFeedbackToLastResponse,
    isLoading,
    setIsLoading,
    error,
    setError,
    setInterviewStage,
    resetInterview,
    setOverallFeedbackText,
    setOverallFeedbackError,
  } = useInterviewContext();
  const navigate = useNavigate();

  const [currentAnswerText, setCurrentAnswerText] = useState(null);
  const [showFeedback, setShowFeedback] = useState(false);

  const pageTitleRef = useRef(null);
  const questionDisplayRef = useRef(null);
  const feedbackPanelRef = useRef(null);
  const errorDisplayRef = useRef(null);


  useEffect(() => {
    if (pageTitleRef.current) {
      pageTitleRef.current.focus();
    }
  }, []);

  useEffect(() => {
    if (error && errorDisplayRef.current) {
        errorDisplayRef.current.focus();
    } else if (showFeedback && feedbackPanelRef.current) {
        feedbackPanelRef.current.focus();
    } else if (questionDisplayRef.current) { // Focus on new question
        questionDisplayRef.current.focus();
    }
  }, [error, showFeedback, currentQuestionIndex, questions]);


  useEffect(() => {
    if (!questions || questions.length === 0 || !cvData.sessionId) {
      // setError(
      //   "Interview session is not properly initialized. Missing questions or session ID."
      // );
      navigate("/interview/setup", { replace: true });
    }
  }, [questions, cvData.sessionId, navigate, setError]);

  const handleTranscriptionComplete = async (
    audioUrl,
    recordedTime,
    audioBlob
  ) => {
    const currentQuestionObject = questions[currentQuestionIndex];
    if (!currentQuestionObject || !currentQuestionObject.id) {
      setError(
        "Current question is not available or missing an ID. Please restart."
      );
      return;
    }
    if (!cvData.sessionId) {
      setError(
        "Session ID is missing. Cannot evaluate answer. Please restart."
      );
      return;
    }

    let retries = 0;
    let success = false;
    let backendResponse = null;

    setIsLoading((prev) => ({
      ...prev,
      transcription: true,
      evaluation: true,
    }));
    setError(null);

    while (retries < MAX_RETRIES && !success) {
      try {
        const answerEvaluationResponseDto = await evaluateAnswer(
          currentQuestionObject.id,
          cvData.sessionId,
          audioBlob
        );

        backendResponse = answerEvaluationResponseDto?.data;

        if (
          !backendResponse ||
          backendResponse.transcribedAnswer === undefined
        ) {
          throw new Error(
            "Incomplete data received from evaluation service. Transcribed answer is missing."
          );
        }

        success = true;
      } catch (err) {
        retries++;
        if (retries >= MAX_RETRIES) {
          console.error("Evaluation error after retries:", err);
          setError(
            `Failed to get feedback after ${MAX_RETRIES} attempts: ${
              err.message || "An unknown error occurred."
            }`
          );
          setShowFeedback(false); // Ensure feedback isn't shown on final failure
          setIsLoading((prev) => ({
            ...prev,
            transcription: false,
            evaluation: false,
          }));
          return; // Exit if all retries fail
        }
        // Optionally add a small delay before retrying
        // await new Promise(resolve => setTimeout(resolve, 1000));
      }
    }

    // This block should only execute if success is true OR if retries exhausted and we want to show partial data.
    // Given the return above, it only runs on success.
    const transcribedAnswer = backendResponse.transcribedAnswer || "";
    let aiFeedback = null;

    if (backendResponse.aiFeedbackJson) {
      try {
        aiFeedback = JSON.parse(backendResponse.aiFeedbackJson);
      } catch (parseError) {
        console.error("Failed to parse AI Feedback JSON:", parseError);
        setError(
          "Feedback received, but could not parse it. Displaying transcript only for this question."
        );
        aiFeedback = { // Provide a structured error feedback object
          error: "Failed to parse feedback JSON.",
          rawFeedback: backendResponse.aiFeedbackJson, // For debugging or potential display
          overallAssessment: // Generic message
            "There was an issue processing the feedback for this answer.",
        };
      }
    }

    setCurrentAnswerText(transcribedAnswer);
    addResponse(currentQuestionObject, transcribedAnswer, audioUrl);
    addFeedbackToLastResponse(aiFeedback); // This might be null or the error feedback object
    setShowFeedback(true);
    setIsLoading((prev) => ({
      ...prev,
      transcription: false,
      evaluation: false,
    }));
  };

  const handleNextQuestion = async () => {
    setError(null); // Clear previous errors
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex((prev) => prev + 1);
      setCurrentAnswerText(null);
      setShowFeedback(false);
    } else {
      // Last question answered, get overall feedback then navigate
      setIsLoading((prev) => ({ ...prev, overallFeedback: true }));
      setOverallFeedbackError(null); // Clear previous overall feedback errors
      setOverallFeedbackText(null); // Clear previous overall feedback text
      try {
        if (!cvData.sessionId) {
          throw new Error(
            "Session ID is missing. Cannot fetch overall feedback."
          );
        }
        const feedbackText = await getOverallInterviewFeedback(
          cvData.sessionId
        );
        setOverallFeedbackText(feedbackText);
      } catch (err) {
        console.error("Overall feedback error:", err);
        setOverallFeedbackError(
          err.message || "Failed to load overall feedback."
        );
      } finally {
        setIsLoading((prev) => ({ ...prev, overallFeedback: false }));
        setInterviewStage("finished");
        navigate("/interview/summary");
      }
    }
  };

  const handleStartOver = () => {
    resetInterview();
    navigate("/interview/setup");
  };

  const handleGoHome = () => {
    resetInterview();
    navigate("/");
  };

  if (
    !questions ||
    questions.length === 0 ||
    !questions[currentQuestionIndex] ||
    !cvData.sessionId
  ) {
    return (
      <main className="min-h-screen bg-slate-900 text-slate-100 py-8 px-4 text-center flex flex-col justify-center items-center">
        <div role="status" aria-live="polite">
          <Loader message="Loading interview session..." />
        </div>
        {error && <p ref={errorDisplayRef} tabIndex={-1} className="text-red-300 mt-4 outline-none">{error}</p>}
        <p className="text-slate-400 mt-4">
          If this takes too long, please{" "}
          <button
            onClick={handleStartOver}
            className="text-sky-400 hover:underline"
          >
            start over
          </button>
          .
        </p>
      </main>
    );
  }

  const lastResponseWithFeedback =
    userResponses.length > 0
      ? userResponses[userResponses.length - 1]?.feedback
      : null;
  const progress = ((currentQuestionIndex + 1) / questions.length) * 100;
  const progressText = `Question ${currentQuestionIndex + 1} of ${questions.length}`;

  return (
    <main className="min-h-screen bg-slate-900 text-slate-100 py-8 px-4">
      <div className="max-w-3xl mx-auto"> {/* Slightly reduced max-width for typical content pages */}
        <div className="flex justify-between items-center mb-4">
          <div>
            <h1 ref={pageTitleRef} tabIndex={-1} className="text-2xl sm:text-3xl font-bold text-slate-50 outline-none">
              Interview Practice
            </h1>
            <p className="text-slate-300 text-sm sm:text-base" aria-hidden="true"> {/* Hidden as progress bar has valuetext */}
              {progressText}
            </p>
          </div>
          <div className="space-x-3">
            <button
              onClick={handleStartOver}
              className="text-slate-400 hover:text-sky-300 text-sm transition-colors"
            >
              Start Over
            </button>
            <button
              onClick={handleGoHome}
              className="text-slate-400 hover:text-sky-300 text-sm transition-colors"
            >
              Go Home
            </button>
          </div>
        </div>

        <div
          role="progressbar"
          aria-valuenow={currentQuestionIndex + 1}
          aria-valuemin="1"
          aria-valuemax={questions.length}
          aria-valuetext={progressText}
          className="w-full bg-slate-700 rounded-full h-2.5 mb-6"
        >
          <div
            className="bg-sky-500 h-2.5 rounded-full transition-all duration-300 ease-out"
            style={{ width: `${progress}%` }}
          ></div>
        </div>

        {/* Ensure QuestionDisplay focuses its content when question changes via ref if possible */}
        <div ref={questionDisplayRef} tabIndex={-1} className="bg-slate-800 rounded-xl shadow-xl p-6 sm:p-8 mb-6 border border-slate-700 outline-none">
          <QuestionDisplay /> {/* Assume this component is made theme-consistent */}
        </div>

        <div className="bg-slate-800 rounded-xl shadow-xl p-6 sm:p-8 mb-6 border border-slate-700">
          <VoiceRecorder
            key={currentQuestionIndex} // Re-mounts recorder for new question, clearing its state
            onTranscriptionComplete={handleTranscriptionComplete}
            disabled={isLoading.evaluation || isLoading.transcription}
          />
        </div>

        {isLoading.transcription && (
          <div role="status" aria-live="polite" className="text-center mb-6">
            <Loader message="Transcribing your answer..." />
          </div>
        )}
        {isLoading.evaluation && !isLoading.transcription && (
          <div role="status" aria-live="polite" className="text-center mb-6">
            <Loader message="Analyzing response and generating feedback..." />
          </div>
        )}

        {error && (
          <div role="alert" aria-live="assertive" ref={errorDisplayRef} tabIndex={-1} className="bg-red-700/20 border-l-4 border-red-500 text-red-200 p-4 mb-6 rounded-md outline-none">
            <p className="text-sm font-medium">Error:</p>
            <p className="text-sm">{error}</p>
          </div>
        )}

        {currentAnswerText !== null &&
          !isLoading.evaluation &&
          !isLoading.transcription &&
          (
          <div className="space-y-6">
            <div className="bg-slate-800/70 rounded-xl p-4 sm:p-6 border border-slate-700">
              <h3 className="font-medium text-slate-100 mb-2 text-lg">
                Your Answer (Transcript):
              </h3>
              <p className="text-slate-300 whitespace-pre-wrap text-sm sm:text-base">
                {currentAnswerText || (
                  <span className="italic text-slate-400">
                    No speech detected or transcript unavailable.
                  </span>
                )}
              </p>
            </div>

            {showFeedback &&
              lastResponseWithFeedback &&
                <div ref={feedbackPanelRef} tabIndex={-1} className="bg-slate-800 rounded-xl shadow-xl p-6 sm:p-8 border border-slate-700 outline-none">
                  <FeedbackPanel feedback={lastResponseWithFeedback} /> {/* Assume this component is made theme-consistent */}
                </div>
              }

            {showFeedback && ( // Only show Next/Finish if feedback has been processed (even if feedback had an error)
              <div className="text-center pt-2">
                <button
                  onClick={handleNextQuestion}
                  disabled={isLoading.evaluation || isLoading.overallFeedback}
                  className="px-8 py-3 bg-sky-600 hover:bg-sky-500 text-white font-semibold rounded-xl shadow-lg hover:shadow-sky-500/30 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-150"
                >
                  {isLoading.overallFeedback
                    ? "Finalizing..."
                    : currentQuestionIndex < questions.length - 1
                    ? "Next Question"
                    : "Finish Interview"}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
};

export default InterviewActivePage;
